/** @format */

import fetch from "isomorphic-fetch";
import axios from "axios";
import {
  TAXI_QUOTE_BRANDREF,
  ENABLE_SIMPLELIST_CACHING,
} from "../../config/config";
import * as Sentry from "@sentry/react";

const brandRef = TAXI_QUOTE_BRANDREF;

const BASE_URL = "/api";
const CACHE_DURATION = 24 * 60 * 60 * 1000;

const isCacheValid = (cacheTime) => {
  const now = Date.now();
  const valid = now - cacheTime < CACHE_DURATION;
  return valid;
};

const isClientCachingEnabled =
  ENABLE_SIMPLELIST_CACHING &&
  typeof window !== "undefined" &&
  typeof localStorage !== "undefined";

const fetchData = async (endpoint) => {
  const cacheKey = `cache_${endpoint}`;
  let cached = null;

  if (isClientCachingEnabled) {
    try {
      cached = localStorage.getItem(cacheKey);
    } catch {
      // Silently fail – localStorage may be unavailable or blocked
    }

    if (cached) {
      try {
        const { data, cacheTime } = JSON.parse(cached);
        if (isCacheValid(cacheTime)) {
          return data;
        }
      } catch {
        // Silently ignore malformed cache and proceed to fetch fresh data
      }
    }
  }

  try {
    const response = await fetch(`${BASE_URL}${endpoint}`);
    const jsonData = await response.json();

    if (!jsonData || !Array.isArray(jsonData.simpleListItems)) {
      const errorMessage = `Unexpected data format from ${endpoint}: simpleListItems is missing or not an array.`;
      Sentry.captureMessage(errorMessage, {
        level: "error", // Changed to error as this indicates a serious issue with the API response
        tags: { endpoint, dataType: typeof jsonData.simpleListItems },
        extra: { rawResponse: jsonData }, // Include the raw response for debugging
      });
      throw new Error(errorMessage);
    }

    const formattedData = jsonData.simpleListItems.map((item) => ({
      listItemId: item.listItemId,
      listItemDescription: item.listItemDescription,
    }));

    if (formattedData.length === 0) {
      Sentry.captureMessage(
        `API returned empty list for endpoint: ${endpoint}`,
        {
          level: "warning", // Can be warning or error depending on expected behavior for empty lists
          tags: { endpoint },
        }
      );
    }

    if (isClientCachingEnabled) {
      try {
        localStorage.setItem(
          cacheKey,
          JSON.stringify({ data: formattedData, cacheTime: Date.now() })
        );
      } catch {
        // Ignore setItem failure (e.g. quota exceeded)
      }
    }
    return formattedData;
  } catch (error) {
    const errorTitle = `${error.name}: ${error.message}`;
    if (endpoint === "/vehCover") {
      if (window.hj) {
        hj("event", "vehCover_network_error");
      }
    }
    Sentry.captureMessage(`ListItems API exception error -- ${errorTitle}`, {
      level: "error",
      tags: { endpoint },
    });
    Sentry.captureException(error, {
      tags: { endpoint },
    });
    console.error(`Failed to fetch data from ${endpoint}:`, error);
    throw error;
  }
};

export const fetchEmpBusiness = () => fetchData("/employersBus");
export const fetchEmp = () => fetchData("/employmentStatus");
export const fetchKeeper = () => fetchData("/keeper");
export const fetchLicenceAuth = () => fetchData("/licenceAuth");
export const fetchMartialStatus = () => fetchData("/martial-status");
export const fetchOccupation = () => fetchData("/occupation");
export const fetchOverNight = () => fetchData("/overNight");
export const fetchOwner = () => fetchData("/owner");
export const fetchRelation = () => fetchData("/relationship");
export const fetchSecurity = () => fetchData("/securityDevice");
export const fetchModifications = () => fetchData("/vehiclemod");
export const fetchUberBranch = () => fetchData("/uberBranch");
export const fetchVehicleClaim = () => fetchData("/vehicleclm");
export const fetchVehicleUse = () => fetchData("/vehicleUse");
export const volentryExcess = () => fetchData("/volentryExcess");
export const fetchRating = () => fetchData("/rating");
export const fetchVehCover = () => fetchData("/vehCover");

export const fetchTitlesService = async () => {
  const url = "/api/titles-with-gender";

  const requestBody = {
    AdvancedListName: "titles_with_gender",
    BrandRef: brandRef,
    AdvancedListParameterItems: null,
  };

  try {
    const response = await axios.post(url, requestBody, {});

    return response.data.advancedListItems.map((item) => ({
      listItemId: item.listItemId,
      listItemDescription: item.listItemDescription,
      listItemExtraInfoValue: item.listItemExtraInfoValue,
    }));
  } catch (error) {
    console.error("Error in fetchTitlesService:", error.message);
    throw error;
  }
};

export const fetchNcdService = async () => {
  const cacheKey = "cache_ncd";

  if (isClientCachingEnabled) {
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const { data, cacheTime } = JSON.parse(cached);
        if (Date.now() - cacheTime < 24 * 60 * 60 * 1000) {
          return data;
        }
      }
    } catch {
      // Ignore cache read errors
    }
  }

  try {
    const requestBody = {
      AdvancedListName: "ncd",
      BrandRef: brandRef,
      AdvancedListParameterItems: null,
    };

    const response = await axios.post("/api/ncd", requestBody);
    const data = response.data;

    const formattedData = Array.isArray(data.advancedListItems)
      ? data.advancedListItems.map((item) => ({
          listItemId: item.listItemId,
          listItemDescription: item.listItemDescription,
          listItemExtraInfoValue: item.listItemExtraInfoValue,
        }))
      : [];

    if (isClientCachingEnabled) {
      try {
        localStorage.setItem(
          cacheKey,
          JSON.stringify({ data: formattedData, cacheTime: Date.now() })
        );
      } catch {
        // Ignore cache write errors
      }
    }

    return formattedData;
  } catch (error) {
    console.error("Error in fetchNcdService:", error);
    throw error;
  }
};

export const fetchVehicleRegData = async (VehicleReg) => {
  try {
    const response = await axios.get(`/api/VehicleReg`, {
      params: { VehicleReg },
    });

    return response.data;
  } catch (error) {
    console.error("Error in fetchVehicleRegData:", error);

    if (error.response) {
      throw new Error(`Failed to fetch data: ${error.response.statusText}`);
    } else {
      throw new Error("An unknown error occurred.");
    }
  }
};

export const fetchMakes = async () => {
  try {
    const response = await fetch("/api/make");
    if (!response.ok) {
      throw new Error("Failed to fetch makes.");
    }
    const data = await response.json();
    return data.simpleListItems.map((item) => ({
      listItemId: item.listItemId,
      listItemDescription: item.listItemDescription,
    }));
  } catch (error) {
    console.error("Error fetching makes:", error);
    throw error; // Re-throw to handle it in the component
  }
};

export const fetchModels = async (makeValue) => {
  try {
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");

    const raw = JSON.stringify({
      AdvancedListName: "pv_model",
      BrandRef: brandRef,
      AdvancedListParameterItems: [
        {
          ParameterKey: "MAKECOLUMNNAME",
          ParameterValue: "PV_MODEL_VIEW_MAKE",
        },
        {
          ParameterKey: "MAKECOLUMNVALUE",
          ParameterValue: makeValue,
        },
      ],
    });

    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: raw,
      redirect: "follow",
    };

    const response = await fetch("/api/model", requestOptions);
    if (!response.ok) {
      throw new Error("Failed to fetch models.");
    }

    const data = await response.json();
    return data.advancedListItems.map((item) => ({
      listItemId: item.listItemId,
      listItemDescription: item.listItemDescription,
    }));
  } catch (error) {
    console.error("Error fetching models:", error);
    throw error; // Re-throw to handle it in the component
  }
};

export const fetchVehicleData = async ({
  makeSearch,
  modelSearch,
  vehicleYearValue,
  vehicleDoorvalue,
}) => {
  const requestBody = {
    Make: makeSearch,
    Model: modelSearch,
    YearOfMake: `${vehicleYearValue || ""}`,
    Doors: `${vehicleDoorvalue || ""}`,
  };

  try {
    const response = await fetch("/api/manualVehicles", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error("Response wasn't ok.");
    }

    const result = await response.json();

    return result;
  } catch (error) {
    console.error("Error fetching data:", error);
    if (error.response) {
      console.error("Server Error:", error.response.data);
    } else if (error.request) {
      console.error("No Response:", error.request);
    } else {
      console.error("Request Error:", error.message);
    }
    throw error;
  }
};
